import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import styles from './CheckboxFilterMenu.module.scss'

export interface CheckboxFilterMenuOption {
  value: string
  label: string
  icon?: ReactNode
}

export interface CheckboxFilterMenuProps {
  /** O item que vira o gatilho do menu (p.ex. uma opção do ``Segmented``). */
  children: ReactElement
  /** O título do menu, a partir de quantas opções estão marcadas. */
  title: (shown: number, total: number) => ReactNode
  options: readonly CheckboxFilterMenuOption[]
  /** Valores desmarcados; vazio = todos marcados. */
  hidden: readonly string[]
  onHiddenChange: (hidden: string[]) => void
}

// Tempo para o mouse atravessar o vão entre o item e o menu sem fechá-lo.
const CLOSE_DELAY_MS = 180

/**
 * Menu de checkboxes que abre ao passar o mouse sobre o item que ele
 * envolve, todos marcados de início. O clique no item continua escolhendo a
 * opção; o menu só se abre por hover ou pelo teclado. Não deixa desmarcar o
 * último, e marcar não fecha o menu.
 */
export function CheckboxFilterMenu({ children, title, options, hidden, onHiddenChange }: CheckboxFilterMenuProps) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    closeTimer.current = null
  }, [])
  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      setOpen(false)
    }, CLOSE_DELAY_MS)
  }, [cancelClose])
  useEffect(() => cancelClose, [cancelClose])

  // Aberto por hover, fecha quando o mouse sai do item e do menu. Olha o
  // documento, e não o pointerleave do menu: marcar uma opção re-renderiza a
  // página, e o leave se perdia (o menu ficava aberto).
  useEffect(() => {
    if (!open) return
    function onPointerMove(event: PointerEvent) {
      if (event.pointerType !== 'mouse') return
      const target = event.target as Node | null
      const inside =
        target != null && (triggerRef.current?.contains(target) || contentRef.current?.contains(target))
      if (inside) cancelClose()
      else if (closeTimer.current === null) scheduleClose()
    }
    document.addEventListener('pointermove', onPointerMove)
    return () => {
      document.removeEventListener('pointermove', onPointerMove)
    }
  }, [open, cancelClose, scheduleClose])

  const hiddenSet = new Set(hidden)
  const shown = options.filter((o) => !hiddenSet.has(o.value)).length

  function toggle(value: string, checked: boolean) {
    const next = new Set(hidden)
    if (checked) next.delete(value)
    else next.add(value)
    // Desmarcar todos deixaria a lista vazia sem motivo aparente.
    if (next.size >= options.length) return
    onHiddenChange([...next])
  }

  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        ref={triggerRef}
        asChild
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            cancelClose()
            setOpen(true)
          }
        }}
        // O Radix abre e fecha no pointerdown; aqui o clique só escolhe a opção.
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse') event.preventDefault()
        }}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={contentRef}
        align="start"
        // Sem roubar o foco do seletor quando abre por hover.
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DropdownMenuLabel>{title(shown, options.length)}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((o) => {
          const checked = !hiddenSet.has(o.value)
          return (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={checked}
              disabled={checked && shown === 1}
              onCheckedChange={(next) => {
                toggle(o.value, next)
              }}
              // Marcar uma opção não fecha o menu.
              onSelect={(event) => {
                event.preventDefault()
              }}
            >
              {o.icon ? (
                <span className={styles.optionIcon} aria-hidden>
                  {o.icon}
                </span>
              ) : null}
              {o.label}
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}
